<template>
  <div class="full-width">
    <div class="clickable" v-if="!displayAll" @click="displayAll=true">
      <h3>{{lesson.title}}</h3>
      <div>{{lesson.description}}</div>
    </div>
    <div v-else class="full-width">
      <h-input type="text" required label="Title" v-model="lesson.title" />
      <h-textarea required label="Description" v-model="lesson.description" ws="3"/>
      <h-select :items="typeSelections" label="Select type of resource" v-model="lesson.type" 
        class="small-bottom"></h-select>
      <div v-if="lesson.file">
        {{lesson.file_name}}
      </div>
      <div v-if="lesson.type == 'video'">
        <input type="file" accept="video/*" ref="uploadBox" name="uploadFile"
        class="input-file" @change="checkFileSize($event)">
        <div><i>We recommend you use youtube to host your videos. This ensures video quality and fast upload times.</i></div>
      </div>
      <input type="file" v-if="lesson.type == 'pdf'" accept="application/pdf" ref="uploadBox" name="uploadFile"
        class="input-file" @change="checkFileSize($event)"/>
      <input type="file" v-if="lesson.type == 'audio'" accept="audio/*" ref="uploadBox" name="uploadFile"
        class="input-file" @change="checkFileSize($event)"/>
      <h-textarea type="text" v-if="lesson.type == 'iframe' || lesson.type == 'youtube'" outline label="Full URL" preview="https://youtube.com/?v=WSSWDg6eZ1c" v-model="lesson.iframe_url" hint="https://youtube.com/?v=WSSWDg6eZ1c"/>
      <h-input type="checkbox" label="Published" v-model="lesson.published" />
      <div v-if="lesson.id" class="resources">
        <h4>Additional Resources</h4>
        <div v-for="resource in lesson.resources" :key="resource.id" class="lesson-resources">
          <lesson-resource :resource="resource" />
          <div class="row">
            <additional-resource :resource="resource" style="margin-right: 10px"><button>Edit</button></additional-resource>
            <button @click="removeResource(resource)">Delete</button>
          </div>
        </div>
        <additional-resource :resource="newResource"  @saved="addNewResource"><h-btn flat>Add Additional Resource</h-btn></additional-resource>
      </div>
      <v-alert type="error" :value="error">{{error}}</v-alert>
      <div v-if="saving">
        Saving....</div>
      <h-btn v-if="!saving" color="primary" @click="save" >Save</h-btn>
      <h-btn color="primary" disabled v-else>
        Saving
        <v-progress-circular
          style="margin-left: 5px"
          indeterminate
          color="purple"
          :size="20"
        ></v-progress-circular>
      </h-btn>
      <h-btn @click="destroy">Delete</h-btn>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import axios from "axios";
import dataStore from "@/services/api/store";
import snackbarHub from "@/services/hubs/snackbar";
import additionalResource from "./additionalResource";
import lessonResource from "@/components/lessonResource";

const UPLOAD_URL_ENDPOINT = `${
  process.env.VUE_APP_API_HOST
}api/coaches/module-lessons/`;
const MODULE_LESSON_STORE = "CoachModuleLessons";
const RESOURCE_STORE = "CoachLessonResources";
const MAX_FILE_SIZE_IN_BYTES = 1000000000; // 1 gig

export default {
  props: ["id", "ordering"],
  data() {
    return {
      displayAll: !this.id,
      saving: false,
      filetypes: ["application/pdf", "video/*"],
      newResource: {},
      typeSelections: ["audio", "pdf", "iframe", "video", "youtube"],
      error: "",
      lesson: {
        module: this.$route.params.id,
        type: "",
        ordering: this.ordering,
        resources: []
      }
    };
  },
  components: {
    additionalResource,
    lessonResource
  },
  created() {
    this.lessonId = this.id;
    if (this.id) {
      dataStore.find(MODULE_LESSON_STORE, this.lessonId).then(r => {
        this.newResource.exercise = r.id;
        this.lesson = r;
      });
    }
  },
  methods: {
    checkFileSize() {
      const file = this.$refs.uploadBox && this.$refs.uploadBox.files[0];
      if (file && file.size > MAX_FILE_SIZE_IN_BYTES) {
        this.error = "File size is too large (max is 1GB)";
        return false;
      }
      return true;
    },
    removeResource(resource) {
      snackbarHub.update("Saving");
      dataStore.destroy(RESOURCE_STORE, resource.id).then(() => {
        this.lesson.resources = this.lesson.resources.filter(
          r => r.id !== resource.id
        );
        snackbarHub.update("Saved");
      });
    },
    addNewResource(resource) {
      this.lesson.resources = [...this.lesson.resources, resource];
      this.newResource = { exercise: this.lesson.id };
    },
    save() {
      this.error = "";
      let form = new FormData();
      form.append("module", this.lesson.module);
      form.append("published", !!this.lesson.published);
      form.append("title", this.lesson.title);
      form.append("ordering", this.lesson.ordering);
      form.append("description", this.lesson.description);
      form.append("type", this.lesson.type);
      const file = this.$refs.uploadBox && this.$refs.uploadBox.files[0];
      if (file) {
        if (!this.checkFileSize()) {
          return;
        }
        const name = file.name;
        form.append("file", file, name);
        form.append("file_name", name);
      } else {
        form.append("iframe_url", this.lesson.iframe_url);
      }

      let method = "post",
        endpoint = UPLOAD_URL_ENDPOINT;
      if (this.lesson.id) {
        method = "patch";
        endpoint = UPLOAD_URL_ENDPOINT + this.lesson.id + "/";
      }
      this.saving = true;
      snackbarHub.update("Saving");
      axios[method](endpoint, form)
        .then(({ data }) => {
          this.lesson = data;
          snackbarHub.update("Saved");
          this.$emit(method, data);
          this.$emit("exerciseSaved");
        })
        .catch(({ response }) => {
          if (response.status < 410) {
            if (response.data.type) {
              this.error = "Please select type of lesson this is";
              return;
            }
            this.error = response.data;
          } else if (response.status === 413) {
            this.error = "File size too large (max is 1GB)";
          } else {
            this.error = "There was a server error. Our staff has been notified";
          }
          snackbarHub.update("Error");
        })
        .then(() => {
          this.saving = false;
          this.displayAll = false;
        });
    },
    destroy() {
      if (this.lessonId) {
        this.lesson.destroy().then(() => {
          this.$emit("deleted", this.lesson);
          snackbarHub.update("Deleted");
        });
      }
    }
  }
};
</script>

<style lang="less">
.resources {
  padding-top: 10px;
  padding-bottom: 10px;
}
.lesson-resources {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  .btns {
    display: flex;
  }
}
</style>
